import type { ViewportBounds, WindowExtras, WindowPoint, WindowSize, WindowState } from './types';

const WINDOW_STATE_KEY_PREFIX = 'ui.windowState.';
let windowPersistenceScope: string | null = null;

export function setWindowPersistenceScope(scope: string | null): void {
    const normalized = scope?.trim() ?? '';
    windowPersistenceScope = normalized.length > 0 ? normalized : null;
}

export function getWindowPersistenceScope(): string | null {
    return windowPersistenceScope;
}

function scopedKey(windowId: string): string | null {
    if (!windowPersistenceScope) return null;
    return `${WINDOW_STATE_KEY_PREFIX}${windowPersistenceScope}.${windowId}`;
}

function isFiniteNumber(value: unknown): value is number {
    return typeof value === 'number' && Number.isFinite(value);
}

function sanitizeSize(
    rawSize: unknown,
    defaults: WindowSize,
    minWidth: number,
    minHeight: number,
    viewport: ViewportBounds
): WindowSize {
    const fallbackWidth = Math.max(minWidth, defaults.width);
    const fallbackHeight = Math.max(minHeight, defaults.height);
    if (!rawSize || typeof rawSize !== 'object') {
        return {
            width: Math.min(fallbackWidth, viewport.width),
            height: Math.min(fallbackHeight, viewport.height),
        };
    }

    const size = rawSize as { width?: unknown; height?: unknown };
    const width = isFiniteNumber(size.width) ? size.width : fallbackWidth;
    const height = isFiniteNumber(size.height) ? size.height : fallbackHeight;
    return {
        width: Math.min(Math.max(minWidth, width), viewport.width),
        height: Math.min(Math.max(minHeight, height), viewport.height),
    };
}

function clampPosition(position: WindowPoint, size: WindowSize, viewport: ViewportBounds): WindowPoint {
    const maxX = Math.max(0, viewport.width - size.width);
    const maxY = Math.max(0, viewport.height - size.height);
    return {
        x: Math.min(Math.max(0, position.x), maxX),
        y: Math.min(Math.max(0, position.y), maxY),
    };
}

function sanitizePosition(rawPosition: unknown, defaults: WindowPoint, size: WindowSize, viewport: ViewportBounds): WindowPoint {
    if (!rawPosition || typeof rawPosition !== 'object') {
        return clampPosition(defaults, size, viewport);
    }

    const position = rawPosition as { x?: unknown; y?: unknown };
    const x = isFiniteNumber(position.x) ? position.x : defaults.x;
    const y = isFiniteNumber(position.y) ? position.y : defaults.y;
    return clampPosition({ x, y }, size, viewport);
}

function sanitizeState(
    raw: unknown,
    defaults: WindowState,
    minWidth: number,
    minHeight: number,
    viewport: ViewportBounds
): WindowState {
    const rawObj = raw && typeof raw === 'object' ? (raw as { size?: unknown; position?: unknown }) : {};
    const size = sanitizeSize(rawObj.size, defaults.size, minWidth, minHeight, viewport);
    const position = sanitizePosition(rawObj.position, defaults.position, size, viewport);
    return { position, size };
}

export function loadWindowState(
    windowId: string,
    defaults: WindowState,
    minWidth: number,
    minHeight: number,
    viewport: ViewportBounds = { width: window.innerWidth, height: window.innerHeight }
): WindowState {
    const key = scopedKey(windowId);
    if (!key) {
        return sanitizeState(undefined, defaults, minWidth, minHeight, viewport);
    }
    const raw = window.localStorage.getItem(key);
    if (!raw) {
        return sanitizeState(undefined, defaults, minWidth, minHeight, viewport);
    }

    try {
        return sanitizeState(JSON.parse(raw), defaults, minWidth, minHeight, viewport);
    } catch {
        return sanitizeState(undefined, defaults, minWidth, minHeight, viewport);
    }
}

export function loadWindowExtras(windowId: string, defaults: WindowExtras = {}): WindowExtras {
    const key = scopedKey(windowId);
    if (!key) return { ...defaults };
    const raw = window.localStorage.getItem(key);
    if (!raw) return { ...defaults };
    try {
        const parsed = JSON.parse(raw) as { extras?: unknown };
        if (!parsed.extras || typeof parsed.extras !== 'object') return { ...defaults };
        return { ...defaults, ...(parsed.extras as WindowExtras) };
    } catch {
        return { ...defaults };
    }
}

export function saveWindowState(windowId: string, state: WindowState, extras: WindowExtras = {}): void {
    const key = scopedKey(windowId);
    if (!key) return;
    // Persist as a single object for atomic read/write:
    // { position: {x,y}, size: {width,height}, extras: {...} }
    const payload = JSON.stringify({
        position: { x: state.position.x, y: state.position.y },
        size: { width: state.size.width, height: state.size.height },
        extras,
    });
    try {
        window.localStorage.setItem(key, payload);
    } catch {
        // Ignore storage errors (quota/private mode).
    }
}
